import { useLocalObservable } from 'mobx-react-lite';
import { ElementType } from 'react';

import { IcSiteIcon, IcSupportIcon } from '@gem/icons';
import { useTranslation } from '@gem/modules/i18n';
import { ICommonPopupsProps } from '@gem/popups';

import Tasks from 'APP/Tasks';

import { DevToolsPopup } from '../DevToolsPopup/DevToolsPopup';
import { IDevToolsPopupParams } from '../DevToolsPopup/DevToolsPopup.presenter';

interface IMenuItem {
  key: string;
  title: string;
  Icon: ElementType;
  visible: boolean;
  onClick(): void;
}

interface IAboutPopupPresenter {
  version: string;
  menuItems: IMenuItem[];
  hasBackButton: boolean;
  isShowDevTool: boolean;
  clickCount: 0;
  onSupport(): void;
  onDevTools(): void;
  onWebsite(): void;
  onClose(): void;
  onBack(): void;
  onVersionClick(): void;
}

const CLICK_DEV_TOOL_AMOUNT = 10;
const LOCAL_STORAGE_DEV_KEY = 'isDevToolEnabled';

export function useAboutPopupPresenter({
  popupInstance,
  onClose,
  onBack,
}: ICommonPopupsProps): IAboutPopupPresenter {
  const { t } = useTranslation();

  const presenter = useLocalObservable<IAboutPopupPresenter>(() => ({
    clickCount: 0,
    isShowDevTool: localStorage.getItem(LOCAL_STORAGE_DEV_KEY) === 'true',

    get version(): string {
      return window.app.version;
    },

    get menuItems(): IMenuItem[] {
      return [
        {
          key: 'contact-support',
          Icon: IcSupportIcon,
          title: t('about_contact_support'),
          onClick: presenter.onSupport,
          visible: true,
        },
        {
          key: 'website',
          Icon: IcSiteIcon,
          title: t('about_website'),
          onClick: presenter.onWebsite,
          visible: true,
        },
        {
          key: 'devtools',
          Icon: '' as ElementType,
          title: 'Dev tools',
          onClick: presenter.onDevTools,
          visible: presenter.isShowDevTool,
        },
      ].filter((item) => item.visible);
    },

    onSupport(): void {
      Tasks.app.openSupport();
    },

    onWebsite(): void {
      window.open(t('about_link'), '_blank');
    },

    onDevTools(): void {
      popupInstance.close();
      Tasks.app.openPopup<IDevToolsPopupParams>(DevToolsPopup, {
        params: { devToolKey: LOCAL_STORAGE_DEV_KEY },
      });
    },

    get hasBackButton(): boolean {
      return !!onBack;
    },

    onClose(): void {
      popupInstance.close();
      onClose?.();
    },

    onBack(): void {
      popupInstance.close();
      onBack?.();
    },

    onVersionClick(): void {
      if (this.clickCount <= CLICK_DEV_TOOL_AMOUNT) {
        this.clickCount++;
        return;
      }
      this.isShowDevTool = true;

      localStorage.setItem(LOCAL_STORAGE_DEV_KEY, 'true');
    },
  }));

  return presenter;
}
